/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useImperativeHandle } from 'preact/hooks';
import { forwardRef, useRef } from 'preact/compat';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { useActionable } from '../hooks/UNSAFE_useActionable';
import { classNames } from '../utils/UNSAFE_classNames';
import { DimensionProps, dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { PaddingProps, paddingInterpolations } from '../utils/UNSAFE_interpolations/padding';
import { ColorProps, colorInterpolations } from '../utils/UNSAFE_interpolations/colors';
import { AriaProps, ariaInterpolations } from '../utils/UNSAFE_interpolations/aria';
import { ActionDetail } from '../UNSAFE_BaseButton';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { baseStyle, hoverStyle, activeStyle } from './themes/ActionCardStyles.css';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';

type StyleProps = DimensionProps & PaddingProps & ColorProps & AriaProps;

type Props = StyleProps &
  TestIdProps & {
    children?: ComponentChildren;

    /**
     * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
     */
    onAction?: (details: ActionDetail) => void;
  };

const interpolations = [
  ...Object.values(dimensionInterpolations),
  ...Object.values(colorInterpolations),
  ...Object.values(paddingInterpolations),
  ...Object.values(ariaInterpolations)
];

const mergedInterpolations = mergeInterpolations<Props>(interpolations);

/**
 * An action card is a container that renders a group of related information in a styled rectangular area, provides enhanced styling for hover and focus states, and invokes an action event when clicked.
 * Width, height, padding, and background color are set through props.
 */
export const ActionCard = forwardRef(({ children, onAction, testId, ...rest }: Props, ref) => {
  const { isTabbable } = useTabbableMode();

  const pressHandler = (e: Event) => {
    if (onAction) {
      onAction({
        reason: e.type == 'keyup' ? 'keyboard' : 'pointer'
      });
    }
  };

  const { actionableProps, isHover, isActive } = useActionable(pressHandler);

  const {
    class: mergedClasses,
    ariaLabel,
    ariaLabelledBy,
    ...mergedStyles
  } = mergedInterpolations(rest);
  const actionStyles = classNames([
    baseStyle,
    mergedClasses,
    isHover && hoverStyle,
    isActive && activeStyle
  ]);
  const rootRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      rootRef.current?.focus();
    },
    blur: () => {
      rootRef.current?.blur();
    }
  }));
  const testIdProps = useTestId(testId);

  return (
    <div
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      ref={rootRef}
      {...actionableProps}
      class={actionStyles}
      tabIndex={isTabbable ? 0 : -1}
      role="button"
      style={mergedStyles}
      {...testIdProps}>
      {children}
    </div>
  );
});
